import { useState, useEffect } from 'react';
import { ArrowLeft, DollarSign, Calendar as CalendarIcon, Plus, Edit, Trash2, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const TennisRacket = ({ size = 24, color = "currentColor" }: { size?: number, color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Strings */}
    <g strokeWidth="1" opacity="0.4">
      <line x1="11.5" y1="12.5" x2="18.5" y2="5.5" />
      <line x1="10.5" y1="9.5" x2="15.5" y2="4.5" />
      <line x1="14.5" y1="13.5" x2="19.5" y2="8.5" />
      <line x1="11.5" y1="5.5" x2="18.5" y2="12.5" />
      <line x1="14.5" y1="5.5" x2="19.5" y2="10.5" />
      <line x1="10.5" y1="7.5" x2="15.5" y2="12.5" />
    </g>
    {/* Racket Head */}
    <ellipse cx="15" cy="9" rx="5" ry="7" transform="rotate(45 15 9)" />
    {/* Handle */}
    <line x1="10.5" y1="13.5" x2="3" y2="21" strokeWidth="2.5" />
    {/* Grip Tape Details */}
    <line x1="5" y1="17" x2="7" y2="19" strokeWidth="1" />
    <line x1="7" y1="15" x2="9" y2="17" strokeWidth="1" />
    {/* Butt cap */}
    <line x1="2" y1="20" x2="4" y2="22" strokeWidth="3" />
  </svg>
);

export const RacketCollection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = location.state?.role || 'ENCORDOADOR';
  const { currentUser } = useAuth();
  
  const API_URL = import.meta.env.VITE_API_URL || 'https://techtennis-api.vercel.app';
  const getAuthHeader = () => {
    const t = localStorage.getItem('tt_auth_token');
    return t ? { 'Authorization': `Bearer ${t}` } : {} as HeadersInit;
  };

  const [jobs, setJobs] = useState<any[]>([]);
  const [professors, setProfessors] = useState<any[]>([]);
  const [appSettings, setAppSettings] = useState<any>({});
  const [manualEntries, setManualEntries] = useState<any[]>([]);
  const [redemptions, setRedemptions] = useState<any[]>([]);
  
  const fetchManualEntries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/manual-entries`, { headers: getAuthHeader() });
      if (res.ok) setManualEntries(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchRedemptions = (profId?: string, forceProfessors?: any[]) => {
    const bffUrl = import.meta.env.VITE_BFF_URL || 'https://techtennis-bff.vercel.app';
    const profsList = forceProfessors || professors;
    const currentProf = profId ? profsList.find((p: any) => p.id === profId) : null;
    let url = `${bffUrl}/api/redemptions`;
    if (profId) {
      url += `?professorId=${profId}`;
      if (currentProf?.numericId) {
        url += `&numericId=${currentProf.numericId}`;
      }
    }
    
    fetch(url)
      .then(r => r.json())
      .then(d => setRedemptions(d))
      .catch(e => console.error(e));
  };
  
  useEffect(() => {
    fetch(`${API_URL}/api/jobs`, { headers: getAuthHeader() }).then(r => r.json()).then(d => setJobs(d)).catch(e => console.error(e));
    fetch(`${API_URL}/api/professors`, { headers: getAuthHeader() }).then(r => r.json()).then(d => {
       setProfessors(d.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })));
       if (role === 'PROFESSOR' && d.length > 0) {
          const myProf = d.find((p: any) => 
            (p.email && currentUser?.email && p.email.toLowerCase() === currentUser?.email.toLowerCase()) || 
            (currentUser?.numericId && p.numericId === currentUser?.numericId) ||
            (p.userId && currentUser?.id && p.userId === currentUser?.id) ||
            (p.linkedUserId && currentUser?.id && p.linkedUserId === currentUser?.id)
          ) || d[0];
          setSelectedProfessorId(myProf.id);
          fetchRedemptions(myProf.id, d);
       } else {
          fetchRedemptions();
       }
    }).catch(e => console.error(e));
    fetch(`${API_URL}/api/settings`, { headers: getAuthHeader() }).then(r => r.json()).then(d => setAppSettings(d)).catch(e => console.error(e));
    fetchManualEntries();
  }, [role]);
// Removed local storage initialization for professors and settings

  const [activeTab, setActiveTab] = useState<'extrato' | 'politica' | 'aprovacoes'>('extrato');
  const [selectedProfessorId, setSelectedProfessorId] = useState<string>('');
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const [isRedemptionModalOpen, setIsRedemptionModalOpen] = useState(false);
  const [redemptionAmount, setRedemptionAmount] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualCustomer, setManualCustomer] = useState('');
  const [manualReason, setManualReason] = useState('');

  const handleSaveManualEntry = async () => {
    if (!manualAmount || !selectedProfessorId || selectedProfessorId === 'all') {
      alert('Selecione um professor específico e informe o valor.');
      return;
    }
    const payload = {
      professorId: selectedProfessorId,
      amount: parseFloat(manualAmount),
      date: manualDate,
      customerName: manualCustomer,
      reason: manualReason
    };
    try {
      if (editingEntry && editingEntry.id) {
        await fetch(`${API_URL}/api/manual-entries/${editingEntry.id}`, { method: 'PUT', headers: {...getAuthHeader(), 'Content-Type': 'application/json'}, body: JSON.stringify(payload) });
      } else {
        await fetch(`${API_URL}/api/manual-entries`, { method: 'POST', headers: {...getAuthHeader(), 'Content-Type': 'application/json'}, body: JSON.stringify(payload) });
      }
      fetchManualEntries();
      setIsManualModalOpen(false);
    } catch(e) { console.error(e); }
  };

  const handleDeleteManualEntry = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este lançamento?')) {
      try {
        await fetch(`${API_URL}/api/manual-entries/${id}`, { method: 'DELETE', headers: getAuthHeader() });
        fetchManualEntries();
      } catch(e) { console.error(e); }
    }
  };

  const openManualModal = (entry?: any) => {
    if (entry) {
      setEditingEntry(entry);
      setManualAmount(entry.amount.toString());
      setManualDate(entry.date || '');
      setManualCustomer(entry.customerName || '');
      setManualReason(entry.reason || '');
    } else {
      setEditingEntry(null);
      setManualAmount('');
      setManualDate('');
      setManualCustomer('');
      setManualReason('');
    }
    setIsManualModalOpen(true);
  };

  const handleRequestRedemption = async () => {
    if (!redemptionAmount || !selectedProfessorId || selectedProfessorId === 'all') {
      alert('Selecione um professor e informe o valor.');
      return;
    }
    try {
      const bffUrl = import.meta.env.VITE_BFF_URL || 'https://techtennis-bff.vercel.app';
      const myProf = professors.find((p: any) => p.id === selectedProfessorId);
      await fetch(`${bffUrl}/api/redemptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          professorId: selectedProfessorId,
          numericId: myProf?.numericId ? Number(myProf.numericId) : undefined,
          amount: parseFloat(redemptionAmount)
        })
      });
      setIsRedemptionModalOpen(false);
      setRedemptionAmount('');
      fetchRedemptions(selectedProfessorId);
      alert('Baixa solicitada com sucesso!');
    } catch(e) { console.error(e) }
  };

  const handleApproveRedemption = async (id: string) => {
    try {
      const bffUrl = import.meta.env.VITE_BFF_URL || 'http://localhost:3001';
      const res = await fetch(`${bffUrl}/api/redemptions/${id}/approve`, { method: 'PUT' });
      if (!res.ok) {
         const err = await res.json();
         throw new Error(err.error || 'Erro ao aprovar');
      }
      fetchRedemptions(selectedProfessorId);
      fetchManualEntries();
      alert('Aprovado com sucesso! Desconto aplicado no Bling.');
    } catch(e: any) { console.error(e); alert(`Erro ao aprovar: ${e.message}`) }
  };

  const handleRejectRedemption = async (id: string) => {
    try {
      const bffUrl = import.meta.env.VITE_BFF_URL || 'http://localhost:3001';
      await fetch(`${bffUrl}/api/redemptions/${id}/reject`, { method: 'PUT' });
      fetchRedemptions(selectedProfessorId);
    } catch(e) { console.error(e) }
  };
  
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const [filterMonth, setFilterMonth] = useState<string>(currentMonth.toString().padStart(2, '0'));
  const [filterYear, setFilterYear] = useState<string>(currentYear.toString());

  // Helper
  const calculateCommission = (job: any) => {
    if (!job.paid) return 0;
    
    let total = 0;
    const commissions = appSettings.commissions || [];

    // Calculate stringing component (using 'Encordoamento' as default keyword)
    if (job.basePrice > 0) {
      const stringComm = commissions.find((c: any) => c.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes('encordoamento'));
      if (stringComm) {
        const baseDiscountVal = Number(job.priceDiscountPercent) || 0;
        const baseDiscountAmt = Number(job.priceDiscountValue) || 0;
        let finalBasePrice = (Number(job.basePrice) * (1 - baseDiscountVal / 100)) - baseDiscountAmt;
        if (finalBasePrice < 0) finalBasePrice = 0;
        total += finalBasePrice * (stringComm.percent / 100);
      }
    }

    // Calculate auxiliary services
    if (job.auxServices) {
      job.auxServices.forEach((service: any) => {
        if (service.isActive && service.price > 0) {
          const svcComm = commissions.find((c: any) => c.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === service.type.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
          if (svcComm) {
            const sDiscount = Number(service.discountPercent) || 0;
            const sDiscountVal = Number(service.discountValue) || 0;
            let finalAuxPrice = (Number(service.price) * (1 - sDiscount / 100)) - sDiscountVal;
            if (finalAuxPrice < 0) finalAuxPrice = 0;
            total += finalAuxPrice * (svcComm.percent / 100);
          }
        }
      });
    }

    return total;
  };

  const calculateServiceValue = (job: any) => {
    let total = 0;
    
    if (job.basePrice > 0) {
      const baseDiscountVal = Number(job.priceDiscountPercent) || 0;
      const baseDiscountAmt = Number(job.priceDiscountValue) || 0;
      let finalBasePrice = (Number(job.basePrice) * (1 - baseDiscountVal / 100)) - baseDiscountAmt;
      if (finalBasePrice < 0) finalBasePrice = 0;
      total += finalBasePrice;
    }

    if (job.auxServices) {
      job.auxServices.forEach((service: any) => {
        if (service.isActive && service.price > 0) {
          const sDiscount = Number(service.discountPercent) || 0;
          const sDiscountVal = Number(service.discountValue) || 0;
          let finalAuxPrice = (Number(service.price) * (1 - sDiscount / 100)) - sDiscountVal;
          if (finalAuxPrice < 0) finalAuxPrice = 0;
          total += finalAuxPrice;
        }
      });
    }

    return total;
  };

  const filteredJobs = jobs.filter(job => {
    if (!job.commissionedProfessorId || job.commissionedProfessorId === 'none') return false;
    if (selectedProfessorId !== 'all' && job.commissionedProfessorId !== selectedProfessorId) return false;
    
    // Date Filtering based on job.date (assuming it's a parseable standard format, e.g. YYYY-MM-DD or DD/MM/YYYY)
    // In StringerDashboard it is `new Date().toLocaleDateString('pt-BR')` which is DD/MM/YYYY
    let jobMonth = '';
    let jobYear = '';
    if (job.date && job.date.includes('/')) {
      const parts = job.date.split('/');
      jobMonth = parts[1];
      jobYear = parts[2];
    } else if (job.date) {
      const dateObj = new Date(job.date);
      jobMonth = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      jobYear = dateObj.getFullYear().toString();
    }
    
    if (filterMonth && filterMonth !== 'all' && jobMonth !== filterMonth) return false;
    if (filterYear && filterYear !== 'all' && jobYear !== filterYear) return false;

    return true;
  });

  const filteredManualEntries = manualEntries.filter(entry => {
    if (selectedProfessorId !== 'all' && entry.professorId !== selectedProfessorId) return false;
    
    let entryMonth = '';
    let entryYear = '';
    if (entry.date) {
      const parts = entry.date.split('-');
      if (parts.length >= 3) {
         entryMonth = parts[1];
         entryYear = parts[0];
      }
    }
    
    if (entry.date) {
       if (filterMonth && filterMonth !== 'all' && entryMonth !== filterMonth) return false;
       if (filterYear && filterYear !== 'all' && entryYear !== filterYear) return false;
    }
    return true;
  });

  const unifiedList = [
    ...filteredJobs.map(job => {
      const commission = calculateCommission(job);
      return {
        id: job.id,
        isManual: false,
        displayDate: job.date || 'S/ Data',
        customerName: job.customerName || 'N/A',
        description: job.racketModel || 'Raquetes/Serviços',
        orderCode: job.orderCode || job.id.substring(0,8).toUpperCase(),
        paid: job.paid,
        serviceValue: calculateServiceValue(job),
        credit: commission,
        pickupDate: job.pickupDate,
        professorId: job.commissionedProfessorId,
        commissionAction: job.commissionAction,
        originalData: job
      };
    }),
    ...filteredManualEntries.map(entry => {
      let displayDate = 'S/ Data';
      if (entry.date) {
        const parts = entry.date.split('-');
        if(parts.length===3) displayDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return {
        id: entry.id,
        isManual: true,
        displayDate,
        customerName: entry.customerName || '-',
        description: entry.reason || 'Lançamento Manual',
        orderCode: 'MANUAL',
        paid: true,
        serviceValue: 0,
        credit: Number(entry.amount),
        pickupDate: null,
        professorId: entry.professorId,
        commissionAction: null,
        originalData: entry
      };
    })
  ].sort((a, b) => b.id.localeCompare(a.id));

  const totalCredit = unifiedList.reduce((acc, item) => acc + (item.paid ? item.credit : 0), 0);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '160px 5% 60px', minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: '32px' }}>
      
      {/* Header */}
      <div className="glass-panel" style={{ padding: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/')} style={{ background: 'var(--bg-panel)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 style={{ fontSize: '32px', color: 'white', fontFamily: 'var(--font-heading)', margin: 0 }}>Extrato de Coleta</h1>
            <p style={{ color: 'var(--text-secondary)', marginTop: '8px', marginBottom: 0 }}>Relatório de comissionamento de raquetes</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {role !== 'PROFESSOR' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 600 }}>Selecione o Professor</label>
              <select 
                value={selectedProfessorId} 
                onChange={(e) => setSelectedProfessorId(e.target.value)}
                style={{ padding: '12px 16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', minWidth: '200px' }}
              >
                <option value="">Selecione...</option>
                <option value="all">Todos os Professores</option>
                {professors.map(p => <option key={p.id} value={p.id}>{p.name} {p.numericId ? `(ID: ${p.numericId})` : ''}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 600 }}>Competência (Mês / Ano)</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select 
                value={filterMonth} 
                onChange={(e) => setFilterMonth(e.target.value)}
                style={{ padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
              >
                <option value="all">Todos</option>
                {Array.from({ length: 12 }).map((_, i) => (
                  <option key={i} value={(i + 1).toString().padStart(2, '0')}>{(i + 1).toString().padStart(2, '0')}</option>
                ))}
              </select>
              <select 
                value={filterYear} 
                onChange={(e) => setFilterYear(e.target.value)}
                style={{ padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
              >
                <option value="all">Todos</option>
                {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                  <option key={y} value={y.toString()}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <button
          onClick={() => setActiveTab('extrato')}
          style={{ padding: '12px 24px', background: 'transparent', border: 'none', borderBottom: activeTab === 'extrato' ? '2px solid var(--primary-color)' : '2px solid transparent', color: activeTab === 'extrato' ? 'var(--primary-color)' : 'white', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', fontSize: '16px' }}
        >
          Extrato de Coleta
        </button>
        <button
          onClick={() => setActiveTab('politica')}
          style={{ padding: '12px 24px', background: 'transparent', border: 'none', borderBottom: activeTab === 'politica' ? '2px solid var(--primary-color)' : '2px solid transparent', color: activeTab === 'politica' ? 'var(--primary-color)' : 'white', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', fontSize: '16px' }}
        >
          Política de Comissionamento
        </button>
        <button
          onClick={() => setActiveTab('aprovacoes')}
          style={{ padding: '12px 24px', background: 'transparent', border: 'none', borderBottom: activeTab === 'aprovacoes' ? '2px solid var(--primary-color)' : '2px solid transparent', color: activeTab === 'aprovacoes' ? 'var(--primary-color)' : 'white', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          Solicitações / Aprovações
          {redemptions.filter(r => r.status === 'PENDING').length > 0 && (
             <span style={{ background: '#EB5757', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{redemptions.filter(r => r.status === 'PENDING').length}</span>
          )}
        </button>
      </div>

      {activeTab === 'extrato' && (
        selectedProfessorId ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-panel" style={{ padding: '32px' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ color: 'white', fontSize: '20px', margin: 0 }}>Resultados do Período</h2>
              <div style={{ display: 'flex', gap: '8px' }}>
              {selectedProfessorId && selectedProfessorId !== 'all' && (
                <>
                  <button 
                    onClick={() => setIsRedemptionModalOpen(true)}
                    style={{ background: '#EB5757', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    Solicitar Baixa de Crédito
                  </button>
                  {role !== 'PROFESSOR' && (
                    <button 
                      onClick={() => openManualModal()}
                      style={{ background: 'var(--primary-color)', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <Plus size={18} /> Adicionar Lançamento Manual
                    </button>
                  )}
                </>
              )}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '32px' }}>
              <div style={{ padding: '24px', background: 'var(--bg-panel-solid)', borderRadius: '16px', borderLeft: '4px solid #4298E7' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 600 }}>Total de Raquetes (Mês)</span>
                  <TennisRacket size={20} color="#4298E7" />
                </div>
                <span style={{ color: 'white', fontSize: '32px', fontWeight: 800 }}>{filteredJobs.length}</span>
              </div>
              
              <div style={{ padding: '24px', background: 'var(--bg-panel-solid)', borderRadius: '16px', borderLeft: '4px solid #6FCF97' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 600 }}>Crédito Gerado (Mês)</span>
                  <DollarSign size={20} color="#6FCF97" />
                </div>
                <span style={{ color: '#6FCF97', fontSize: '32px', fontWeight: 800 }}>R$ {totalCredit.toFixed(2)}</span>
              </div>
            </div>

            {/* List/Table */}
            <div style={{ background: 'var(--bg-panel-solid)', borderRadius: '12px', overflow: 'hidden' }}>
              {unifiedList.length === 0 ? (
                <div style={{ padding: '64px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Nenhuma raquete encontrada para este filtro.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', color: 'white' }}>
                  <thead>
                    <tr style={{ color: 'var(--text-secondary)', fontSize: '13px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' }}>
                      <th style={{ padding: '16px 24px', fontWeight: 600 }}>Entrada</th>
                      <th style={{ padding: '16px 24px', fontWeight: 600 }}>Cliente / Raquete</th>
                      <th style={{ padding: '16px 24px', fontWeight: 600 }}>Previsão de Retirada</th>
                      <th style={{ padding: '16px 24px', fontWeight: 600 }}>Status do Pedido</th>
                      <th style={{ padding: '16px 24px', fontWeight: 600 }}>Ação/Regra</th>
                      <th style={{ padding: '16px 24px', fontWeight: 600, textAlign: 'right' }}>Serviço</th>
                      <th style={{ padding: '16px 24px', fontWeight: 600, textAlign: 'right' }}>Crédito</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unifiedList.map((item) => {
                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: item.isManual ? 'rgba(66, 152, 231, 0.05)' : 'transparent' }}>
                          <td style={{ padding: '16px 24px', fontSize: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <CalendarIcon size={14} color="var(--primary-color)" /> {item.displayDate}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                              Ordem: <span style={{ fontWeight: 600 }}>{item.orderCode}</span>
                            </div>
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            <div style={{ fontWeight: 600, fontSize: '15px' }}>{item.customerName}</div>
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{item.description}</div>
                            {item.isManual && (
                               <span style={{ display: 'inline-block', marginTop: '4px', fontSize: '10px', background: '#3b82f6', color: 'white', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>AJUSTE MANUAL</span>
                            )}
                            {!item.isManual && selectedProfessorId === 'all' && (
                              <div style={{ fontSize: '12px', color: '#E28743', marginTop: '4px', fontWeight: 600 }}>
                                Prof: {(() => {
                                  const prof = professors.find(p => p.id === item.professorId);
                                  return prof ? `${prof.name} ${prof.numericId ? `(ID: ${prof.numericId})` : ''}` : 'Desconhecido';
                                })()}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '16px 24px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                             {item.pickupDate ? new Date(item.pickupDate).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                             <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '100px', fontWeight: 600, background: item.paid ? 'rgba(111,207,151,0.1)' : 'rgba(235,87,87,0.1)', color: item.paid ? '#6FCF97' : '#EB5757' }}>
                               {item.paid ? 'PAGO' : 'AGUARDA PGTO'}
                             </span>
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            {item.isManual ? (
                               <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>-</span>
                            ) : (
                               item.commissionAction === 'COLETA' ? (
                                 <span style={{ display: 'inline-block', padding: '4px 10px', background: 'rgba(66, 152, 231, 0.15)', color: '#4298E7', borderRadius: '100px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px' }}>COLETOU</span>
                               ) : item.commissionAction === 'ATUALIZACAO_SET' ? (
                                 <span style={{ display: 'inline-block', padding: '4px 10px', background: 'rgba(242, 201, 76, 0.15)', color: '#F2C94C', borderRadius: '100px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px' }}>ATUALIZOU SET</span>
                               ) : (
                                 <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>Não informada</span>
                               )
                            )}
                          </td>
                          <td style={{ padding: '16px 24px', textAlign: 'right', fontWeight: 600, color: 'white' }}>
                             {item.isManual ? '-' : `R$ ${item.serviceValue.toFixed(2)}`}
                          </td>
                          <td style={{ padding: '16px 24px', textAlign: 'right', fontWeight: 700, color: item.paid ? '#6FCF97' : 'var(--text-secondary)' }}>
                             {item.credit < 0 ? `- R$ ${Math.abs(item.credit).toFixed(2)}` : `R$ ${item.credit.toFixed(2)}`}
                             {item.isManual && role !== 'PROFESSOR' && (
                               <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                                 <button onClick={() => openManualModal(item.originalData)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#4298E7' }}><Edit size={16} /></button>
                                 <button onClick={() => handleDeleteManualEntry(item.id)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#EB5757' }}><Trash2 size={16} /></button>
                               </div>
                             )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            
          </motion.div>
        ) : (
          <div style={{ padding: '64px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Por favor, selecione um professor no painel acima para carregar o extrato de coleta.
          </div>
        )
      )}

      {activeTab === 'politica' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-panel" style={{ padding: '40px', color: 'white', lineHeight: '1.8' }}>
          <h2 style={{ fontSize: '24px', color: 'var(--primary-color)', marginBottom: '24px' }}>Política de Comissionamento para Professores Parceiros 🎾</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>TechTennisBR – Serviços de Encordoamento</p>

          <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '12px', marginTop: '32px' }}>1. Objetivo do Programa</h3>
          <p style={{ color: 'var(--text-secondary)' }}>
            A TechTennisBR reconhece o papel fundamental dos professores de tênis na orientação de seus alunos. Esta política visa recompensar os professores parceiros que confiam e indicam nossos serviços de encordoamento, criando um ecossistema de benefícios mútuos através de um programa de créditos.
          </p>

          <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '12px', marginTop: '32px' }}>2. Regras para Elegibilidade da Comissão</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Para que o comissionamento sobre o serviço de encordoamento seja validado e creditado, a indicação deve se enquadrar em uma das seguintes modalidades:
          </p>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <li><strong>A) Coleta de Raquetes pelo Professor (Novo Serviço de Retirada):</strong> O professor atua como o facilitador, recolhendo as raquetes com seus alunos. Para trazer mais comodidade aos nossos parceiros, a TechTennis agora oferece um serviço de coleta: nós faremos a retirada diretamente com o professor, sem que ele precise ir até a loja, desde que haja um volume mínimo de 3 raquetes coletadas.</li>
            <li><strong>B) Assessoria Técnica Presencial ao Aluno:</strong> A comissão também será devida sempre que o aluno for pessoalmente à loja por indicação do professor para conversar com o encordoador em busca de assessoria técnica. Esta regra é válida para situações onde o aluno deseja trocar sua configuração de encordoamento ou quando for a sua primeira vez encordoando e ele não souber qual corda e tensão deve usar.</li>
          </ul>
          <p style={{ color: 'var(--text-secondary)', marginTop: '16px' }}>
            <strong>Atenção (Quando a comissão não é devida):</strong> Nos demais casos onde não houver coleta de raquetes feita pelo professor ou quando o aluno for diretamente à loja e não precisar de assessoria especializada do encordoador da TechTennis para decidir o que colocar (ou seja, apenas entregar a raquete já sabendo exatamente qual serviço/encordoamento quer), as comissões não serão devidas.
          </p>

          <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '12px', marginTop: '32px' }}>3. Acompanhamento e Extrato Financeiro</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            A transparência é um pilar da nossa parceria. Todo o valor gerado por comissões não se perde em anotações de papel.
          </p>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <li><strong>Painel Online:</strong> Cada professor parceiro terá acesso a um extrato financeiro exclusivo e atualizado em tempo real na plataforma da TechTennisBR.</li>
            <li><strong>Contabilização:</strong> Assim que o serviço de encordoamento for finalizado e pago, a comissão será automaticamente creditada neste extrato virtual (Extrato de Coleta).</li>
          </ul>

          <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '12px', marginTop: '32px' }}>4. Regras de Resgate e Utilização dos Créditos</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Os valores acumulados no extrato do professor funcionam como um programa de benefícios internos da loja.
          </p>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <li><strong>Uso Exclusivo na TechTennisBR:</strong> O saldo de comissões só poderá ser utilizado para o resgate/pagamento de produtos e serviços oferecidos dentro da própria TechTennisBR (ex: compra de cordas, grips, raquetes, bolas, acessórios, ou encordoamento das próprias raquetes do professor).</li>
            <li><strong>Vedações:</strong> Em nenhuma hipótese haverá o saque, transferência via PIX, ou pagamento diretamente em dinheiro/espécie dos valores acumulados.</li>
          </ul>

          <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '12px', marginTop: '32px' }}>5. Cláusulas Adicionais de Boas Práticas</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Para garantir a excelência e a padronização do serviço perante o cliente final, ficam estabelecidas as seguintes diretrizes:
          </p>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <li><strong>Validade dos Créditos:</strong> Os créditos gerados possuem validade de 12 meses a partir da data em que foram creditados no extrato. Após esse período, os créditos expiram automaticamente.</li>
            <li><strong>Tabela de Preços ao Cliente:</strong> O professor deve praticar e repassar ao aluno os valores oficiais da tabela de encordoamento da TechTennisBR, não sendo permitida a cobrança de sobretaxas em nome da nossa marca.</li>
          </ul>

          <h3 style={{ fontSize: '18px', color: 'white', marginBottom: '12px', marginTop: '32px' }}>6. Comissionamento e Descontos para Professores Parceiros</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Buscando maximizar o retorno da parceria e valorizar os professores ativamente engajados, aplicam-se as seguintes condições comerciais e benefícios de compra:
          </p>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <li><strong>Percentual de Comissionamento:</strong> O professor parceiro terá direito a 10% de comissão sobre o valor total de cada serviço de encordoamento realizado dentro das regras elegíveis (calculado sobre a soma do valor da mão de obra + corda).</li>
            <li><strong>Benefício de Desconto Progressivo em Produtos:</strong> Sempre que o professor efetuar o resgate utilizando R$ 100,00 ou mais do seu saldo acumulado de créditos, ele receberá um desconto especial de 20% sobre o valor dos produtos adquiridos dentro da TechTennisBR.</li>
            <li><strong>Limite do Benefício:</strong> O desconto de 20% é aplicável para compras de produtos até o limite máximo total de R$ 1.500,00 por transação.</li>
          </ul>
        </motion.div>
      )}

      {activeTab === 'aprovacoes' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-panel" style={{ padding: '32px' }}>
           <h2 style={{ color: 'white', fontSize: '20px', margin: 0, marginBottom: '24px' }}>Solicitações de Resgate</h2>
           <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {redemptions.length === 0 ? (
                 <div style={{ color: 'var(--text-secondary)' }}>Nenhuma solicitação encontrada.</div>
              ) : (
                 redemptions.map(r => (
                    <div key={r.id} style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                       <div>
                          <div style={{ color: 'white', fontWeight: 600, fontSize: '16px' }}>Resgate de R$ {r.amount.toFixed(2)}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Professor: {r.customer?.name} - Status: <span style={{ color: r.status === 'PENDING' ? '#F2C94C' : r.status === 'APPROVED' ? '#6FCF97' : '#EB5757' }}>{r.status}</span></div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Data: {new Date(r.createdAt).toLocaleString('pt-BR')}</div>
                       </div>
                       {r.status === 'PENDING' && role !== 'PROFESSOR' && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                             <button onClick={() => handleApproveRedemption(r.id)} style={{ background: '#6FCF97', color: '#1B1B1B', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Aprovar</button>
                             <button onClick={() => handleRejectRedemption(r.id)} style={{ background: 'transparent', color: '#EB5757', border: '1px solid #EB5757', padding: '8px 16px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Rejeitar</button>
                          </div>
                       )}
                    </div>
                 ))
              )}
           </div>
        </motion.div>
      )}


      {isManualModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="glass-panel" style={{ width: '90%', maxWidth: '400px', padding: '32px', borderRadius: '24px', position: 'relative' }}>
            <button onClick={() => setIsManualModalOpen(false)} style={{ position: 'absolute', top: '24px', right: '24px', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}><X size={24} /></button>
            <h2 style={{ color: 'white', marginTop: 0, marginBottom: '24px', fontSize: '24px' }}>{editingEntry ? 'Editar Lançamento' : 'Novo Lançamento'}</h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
               <div>
                  <label style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Valor (R$)* (Use negativo para deduzir)</label>
                  <input type="number" step="0.01" value={manualAmount} onChange={e => setManualAmount(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', outline: 'none' }} placeholder="Ex: 50.00 ou -20.00" />
               </div>
               <div>
                  <label style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Data (Opcional)</label>
                  <input type="date" value={manualDate} onChange={e => setManualDate(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', outline: 'none' }} />
               </div>
               <div>
                  <label style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Cliente / Referência (Opcional)</label>
                  <input type="text" value={manualCustomer} onChange={e => setManualCustomer(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', outline: 'none' }} placeholder="Ex: João Silva" />
               </div>
               <div>
                  <label style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Motivo / Texto (Opcional)</label>
                  <textarea value={manualReason} onChange={e => setManualReason(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', outline: 'none', resize: 'vertical' }} placeholder="Ex: Ajuste de comissão da aula X" />
               </div>
            </div>

            <button onClick={handleSaveManualEntry} className="button-primary" style={{ width: '100%', padding: '16px', borderRadius: '12px', fontWeight: 'bold', border: 'none', cursor: 'pointer', marginTop: '24px', fontSize: '16px' }}>Salvar Lançamento</button>
          </div>
        </div>
      )}

      {isRedemptionModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="glass-panel" style={{ width: '90%', maxWidth: '400px', padding: '32px', borderRadius: '24px', position: 'relative' }}>
            <button onClick={() => setIsRedemptionModalOpen(false)} style={{ position: 'absolute', top: '24px', right: '24px', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}><X size={24} /></button>
            <h2 style={{ color: 'white', marginTop: 0, marginBottom: '24px', fontSize: '24px' }}>Solicitar Baixa de Crédito</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '14px' }}>Informe o valor a ser abatido no caixa. A solicitação será enviada para aprovação do administrador.</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
               <div>
                  <label style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Valor (R$)*</label>
                  <input type="number" step="0.01" value={redemptionAmount} onChange={e => setRedemptionAmount(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', outline: 'none' }} placeholder="Ex: 50.00" />
               </div>
            </div>

            <button onClick={handleRequestRedemption} className="button-primary" style={{ width: '100%', padding: '16px', borderRadius: '12px', fontWeight: 'bold', border: 'none', cursor: 'pointer', marginTop: '24px', fontSize: '16px' }}>Solicitar Aprovação</button>
          </div>
        </div>
      )}

    </div>
  );
};
